import axios from 'axios'
import {
  ProjectDraftDetail,
  ProjectDraftSummary,
  ProjectSummary,
  ProjectUserLookupItem,
  ProjectWizardData,
  ProjectWizardReferenceData,
} from '../models'

const API_URL = `${process.env.REACT_APP_API_URL}/ProjectSetup`
const ROLE_API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/Role` : undefined

export interface SaveDraftPayload {
  id?: number
  name: string
  data: ProjectWizardData
  currentStep: number
}

export interface CreateProjectPayload {
  data: ProjectWizardData
  draftId?: number
}

export interface UpdateProjectPayload {
  data: ProjectWizardData
}

export interface ProjectDetail {
  id: number
  projectNumber: string
  projectName: string
  created: string
  data: ProjectWizardData
  isArchived: boolean
  archivedAt?: string
}

export type RoleLookupItem = {
  id: string
  name?: string
  displayId?: number
}

export const getReferenceData = async () => {
  const { data } = await axios.get<ProjectWizardReferenceData>(`${API_URL}/lookup`)
  return data
}

export const listProgrammeNames = async () => {
  const {data} = await axios.get<string[]>(`${API_URL}/programmes`)
  return data
}

export const listProjectSelectionOptions = async (includeArchived: boolean, signal?: AbortSignal) => {
  const {data} = await axios.get<ProjectSummary[]>(`${API_URL}/project-options`, {params: {includeArchived}, signal})
  return data
}

export const listDrafts = async () => {
  const { data } = await axios.get<ProjectDraftSummary[]>(`${API_URL}/drafts`)
  return data
}

export const getDraft = async (id: number) => {
  const { data } = await axios.get<ProjectDraftDetail>(`${API_URL}/drafts/${id}`)
  return data
}

export const saveDraft = async (payload: SaveDraftPayload) => {
  const { data } = await axios.post<ProjectDraftDetail>(`${API_URL}/drafts`, payload)
  return data
}

export const deleteDraft = async (id: number) => {
  await axios.delete(`${API_URL}/drafts/${id}`)
}

export const createProject = async (payload: CreateProjectPayload) => {
  const { data } = await axios.post<ProjectSummary>(`${API_URL}`, payload)
  return data
}

export const updateProject = async (projectId: number, payload: UpdateProjectPayload) => {
  const { data } = await axios.put<ProjectSummary>(`${API_URL}/projects/${projectId}`, payload)
  return data
}

export const getProjectDetail = async (projectId: number) => {
  const { data } = await axios.get<ProjectDetail>(`${API_URL}/projects/${projectId}`)
  return data
}

export const listProjects = async (limit?: number, searchTerm?: string, archived = false) => {
  const params = new URLSearchParams()
  if (limit) {
    params.append('limit', `${limit}`)
  }

  params.append('archived', `${archived}`)
  const normalizedSearchTerm = searchTerm?.trim()
  if (normalizedSearchTerm) {
    params.append('term', normalizedSearchTerm)
  }

  const { data } = await axios.get<ProjectSummary[]>(`${API_URL}/projects`, { params })
  return data
}

export const searchUsers = async (searchTerm: string, roles: string[]) => {
  const params = new URLSearchParams()
  if (searchTerm) {
    params.append('term', searchTerm)
  }
  roles.forEach((role) => params.append('roles', role))

  const { data } = await axios.get<ProjectUserLookupItem[]>(`${API_URL}/users`, { params })
  return data
}

export const getRoleOptions = async (keyword?: string) => {
  if (!ROLE_API_URL) {
    return [] as RoleLookupItem[]
  }

  const params = new URLSearchParams()
  if (keyword) {
    params.append('keyword', keyword)
  }

  const { data } = await axios.get<RoleLookupItem[]>(`${ROLE_API_URL}/getallroles`, { params })
  return data
}
